// Long dashes that phone keyboards substitute for "--" (iOS smart punctuation
// and some Android keyboards). FIG notation relies on "--", so map them back.
const LONG_DASHES = /[—–]/g;

// Normalize a FIG notation typed by a person before saving or comparing it.
export function normalizeFig(s) {
  return (s || '').trim().replace(LONG_DASHES, '--');
}

// Normalize a search string for skill name / FIG notation matching. Converts
// the unicode fractions and English fraction words into a single ASCII form so
// "1/2 twist", "half twist" and "½ twist" all match the same skills, and long
// dashes back to "--" so "8—o" typed on a phone finds "8--o".
export function normalizeSkillSearch(s) {
  return (s || '')
    .replace(LONG_DASHES, '--')
    .toLowerCase()
    // "1½" reads as "1 1/2", so "1/2 in" finds "1½ In ½ Out" too
    .replace(/(\d)([½¼¾])/g, '$1 $2')
    .replace(/½/g, '1/2')
    .replace(/¼/g, '1/4')
    .replace(/¾/g, '3/4')
    .replace(/\bhalf\b/g, '1/2')
    .replace(/\bquarter\b/g, '1/4')
    .replace(/three[\s-]*quarters?\b/g, '3/4')
    // Shape words match the (T)/(P)/(S) suffix, so "back tuck" finds "Back (T)"
    .replace(/\btuck(ed)?\b/g, '(t)')
    .replace(/\bpiked?\b/g, '(p)')
    .replace(/\bstraight\b/g, '(s)');
}

const words = (s) => normalizeSkillSearch(s).split(/\s+/).filter(Boolean);

// True if every word of the query appears in the name or FIG, in any order.
export function matchesSkillQuery(query, name, fig) {
  const qs = words(query);
  if (!qs.length) return true;
  const hay = normalizeSkillSearch(`${name || ''} ${fig || ''}`);
  return qs.every(w => hay.includes(w));
}

// 0 exact FIG, 1 name starts with query, 2 a name word starts with the first
// query word, 3 anywhere. -1 when it doesn't match at all.
function tier(query, skill) {
  if (!matchesSkillQuery(query, skill.name, skill.figNotation)) return -1;
  const q = words(query).join(' ');
  if (!q) return 3;
  if (skill.figNotation && normalizeSkillSearch(skill.figNotation).trim() === q) return 0;
  const name = normalizeSkillSearch(skill.name);
  if (name.startsWith(q)) return 1;
  const first = words(query)[0];
  if (name.split(/[\s,:()]+/).some(w => w.startsWith(first))) return 2;
  return 3;
}

// Skills matching the query, best first; easiest first within each tier.
// Easiest first, so everyday skills come before rarely used multi-twisters.
// Skills with no difficulty go last; ties fall back to the name.
const ddOf = (skill) => {
  const n = skill.difficulty == null || skill.difficulty === '' ? NaN : Number(skill.difficulty);
  return Number.isNaN(n) ? Infinity : n;
};
const byDifficulty = (a, b) => (ddOf(a) - ddOf(b)) || a.name.localeCompare(b.name);

export function sortByDifficulty(skills) {
  return skills.slice().sort(byDifficulty);
}

export function rankSkills(query, skills) {
  return skills
    .map(skill => ({ skill, t: tier(query, skill) }))
    .filter(x => x.t >= 0)
    .sort((a, b) => a.t - b.t || byDifficulty(a.skill, b.skill))
    .map(x => x.skill);
}
